import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRetailHouseAccount,
  fetchRetailHouseAccount,
  fetchRetailHouseAccounts,
  postRetailHouseAccountItem,
  settleRetailHouseAccount,
  updateRetailHouseAccount,
  type CreateRetailHouseAccountPayload,
  type ListRetailHouseAccountsQuery,
  type PostRetailHouseAccountItemPayload,
  type SettleRetailHouseAccountPayload,
  type UpdateRetailHouseAccountPayload,
} from '@/services/api/retailHouseAccounts.api';

export function useRetailHouseAccounts(
  query: ListRetailHouseAccountsQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: ['retail-house-accounts', query],
    queryFn: () => fetchRetailHouseAccounts(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useRetailHouseAccount(id: string | null) {
  return useQuery({
    queryKey: ['retail-house-accounts', id, 'items'],
    queryFn: () => fetchRetailHouseAccount(id as string),
    staleTime: 30_000,
    enabled: id !== null,
  });
}

export function useCreateRetailHouseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRetailHouseAccountPayload) =>
      createRetailHouseAccount(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-house-accounts'] });
    },
  });
}

export function useUpdateRetailHouseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRetailHouseAccountPayload }) =>
      updateRetailHouseAccount(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['retail-house-accounts', id, 'items'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-house-accounts'] });
    },
  });
}

export function usePostRetailHouseAccountItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PostRetailHouseAccountItemPayload }) =>
      postRetailHouseAccountItem(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['retail-house-accounts', id, 'items'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-house-accounts'] });
    },
  });
}

export function useSettleRetailHouseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SettleRetailHouseAccountPayload }) =>
      settleRetailHouseAccount(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['retail-house-accounts', id, 'items'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-house-accounts'] });
    },
  });
}
